import styled from '@emotion/styled'
import { ALL_COMBINATIONS, CombinationKey, EBonusMaxBet, getCombinationLabel } from '@lbs/shared-rp-types'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { Button, IconButton, MenuItem, TextField } from '@mui/material'
import { FC, useMemo } from 'react'
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister } from 'react-hook-form'
import { formPropsDrawerCrud } from '../../constants/formPropsDrawerCrud'
import { IRpBonus } from '../../prp2boxApi/bonuses'
import { TRpBonusFormData } from './schema'

const bonusFields: Partial<Record<keyof IRpBonus, string>> = {
  name: 'Имя',
  min: 'Минимум',
  max: 'Максимальная ставка',
  maxPayout: 'Максимальная выплата',
}

export const bonusMaxLabels: Partial<Record<EBonusMaxBet, string>> = {
  [EBonusMaxBet.NO_LIMIT]: 'Без лимита',
  [EBonusMaxBet.ANTE]: 'Анте',
}

const bonusMaxOptions: Array<{ value: EBonusMaxBet; label: string }> = (
  Object.values(EBonusMaxBet) as EBonusMaxBet[]
).map((value) => ({
  value,
  label: bonusMaxLabels[value] ?? value,
}))

type TRpBonusFormBaseProps = {
  register: UseFormRegister<TRpBonusFormData>
  errors: FieldErrors<TRpBonusFormData>
  control: Control<TRpBonusFormData>
}

const Container = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: 60% 25% 15%;
  grid-column-gap: 5px;
  grid-row-gap: 10px;
`

const RpBonusFormBase: FC<TRpBonusFormBaseProps> = (props) => {
  const fieldProps = {
    ...formPropsDrawerCrud,
  }

  const { register, errors, control } = props
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'payouts',
  })

  const combinations = useMemo(() => {
    // Порядок комбинаций от старшей к младшей
    const combinationOrder: CombinationKey[] = [
      'ROYAL_FLUSH',
      'K_STRAIGHT_FLUSH',
      'STRAIGHT_FLUSH',
      'K_FOUR_OF_A_KIND',
      'FOUR_OF_A_KIND',
      'K_FULL_HOUSE_THREE',
      'K_FULL_HOUSE_PAIR',
      'FULL_HOUSE',
      'K_FLUSH',
      'FLUSH',
      'K_STRAIGHT',
      'STRAIGHT',
      'K_THREE_OF_A_KIND',
      'THREE_OF_A_KIND',
      'K_PAIR_PLUS_PAIR',
      'TWO_PAIRS',
      'K_PAIR',
      'ONE_PAIR',
      'FIVE_CARDS_ONE_COLOR',
      'ACE_KING_QUEEN',
      'ACE_KING',
      'HIGH_CARD',
    ]

    const allCombinations = Object.keys(ALL_COMBINATIONS).map((key) => ({
      value: key as CombinationKey,
      label: getCombinationLabel(key as CombinationKey, 'ru'),
      order: combinationOrder.indexOf(key as CombinationKey),
    }))

    // Сортируем по порядку, если комбинация не найдена в порядке - ставим в конец
    return allCombinations.sort((a, b) => {
      const orderA = a.order === -1 ? 999 : a.order
      const orderB = b.order === -1 ? 999 : b.order
      return orderA - orderB
    })
  }, [])

  const usedCombinations = useMemo(() => {
    return new Set(fields.map((field) => field.combination))
  }, [fields])

  const handleAddPayout = () => {
    const availableCombination = combinations.find((combo) => !usedCombinations.has(combo.value))

    if (availableCombination) {
      append({ combination: availableCombination.value, multiplier: 1 })
    }
  }

  const getAvailableCombinations = (currentIndex: number) => {
    const currentCombination = fields[currentIndex]?.combination
    return combinations.filter((combo) => combo.value === currentCombination || !usedCombinations.has(combo.value))
  }

  return (
    <>
      <TextField
        {...fieldProps}
        {...register('name')}
        label={bonusFields.name}
        error={!!errors.name}
        helperText={errors.name?.message}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        {...fieldProps}
        {...register('min')}
        label={bonusFields.min}
        error={!!errors.min}
        helperText={errors.min?.message}
        InputLabelProps={{ shrink: true }}
      />
      <Controller
        name="max"
        control={control}
        render={({ field }) => (
          <TextField
            {...fieldProps}
            {...field}
            select
            label={bonusFields.max}
            error={!!errors.max}
            helperText={errors.max?.message}
            InputLabelProps={{ shrink: true }}>
            {bonusMaxOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <TextField
        {...fieldProps}
        {...register('maxPayout')}
        label={bonusFields.maxPayout}
        error={!!errors.maxPayout}
        helperText={errors.maxPayout?.message || 'Пустое значение означает без лимита'}
        InputLabelProps={{ shrink: true }}
      />

      {fields.map((field, index) => (
        <Container key={field.id}>
          <Controller
            name={`payouts.${index}.combination`}
            control={control}
            defaultValue={field.combination}
            render={({ field: controllerField }) => (
              <TextField {...fieldProps} {...controllerField} select label="Комбинация">
                {getAvailableCombinations(index).map((combo) => (
                  <MenuItem key={combo.value} value={combo.value}>
                    {combo.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name={`payouts.${index}.multiplier`}
            control={control}
            defaultValue={field.multiplier}
            render={({ field: controllerField }) => (
              <TextField {...fieldProps} {...controllerField} type="number" label="Множитель" />
            )}
          />
          <IconButton color="error" onClick={() => remove(index)} aria-label="удалить">
            <DeleteIcon />
          </IconButton>
        </Container>
      ))}

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAddPayout}
        fullWidth
        disabled={usedCombinations.size >= combinations.length}>
        Добавить выплату
      </Button>
    </>
  )
}

export default RpBonusFormBase
